import React, { useEffect } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { ThemeProvider } from './app/ThemeContext';
import { CurrencyProvider } from './app/CurrencyContext';

import Landing from './pages/Landing/Landing';
import Login from './pages/Login/Login';
import ForgotPassword from './pages/ForgotPassword/ForgotPassword';
import ResetPassword from './pages/ResetPassword/ResetPassword';
import Register from './pages/Register/Register';
import BlogPostDetail from './pages/BlogPostDetail/BlogPostDetail';
import NotFound from './pages/NotFound/NotFound';

import UserDashboard from './pages/UserDashboard/UserDashboard';
import Kyc from './pages/Kyc/Kyc';
import Funds from './pages/Funds/Funds';
import Team from './pages/Team/Team';
import Rebate from './pages/Rebate/Rebate';
import Rank from './pages/Rank/Rank';
import AdminRanks from './pages/AdminRanks/AdminRanks';
import Support from './pages/Support/Support';
import InvestmentDashboard from './pages/InvestmentDashboard/InvestmentDashboard';
import InrPaymentHistory from './pages/InrPaymentHistory/InrPaymentHistory';
import PartnerProgram from './pages/PartnerProgram/PartnerProgram';
import AccountSettings from './pages/AccountSettings/AccountSettings';
import PortalInvestments from './pages/PortalInvestments/PortalInvestments';
import BctTokenClaims from './pages/BctTokenClaims/BctTokenClaims';
import Meetings from './pages/Meetings/Meetings';
import MyRequests from './pages/MyRequests/MyRequests';

import AdminDashboard from './pages/AdminDashboard/AdminDashboard';
import AdminFunds from './pages/AdminFunds/AdminFunds';
import AdminKyc from './pages/AdminKyc/AdminKyc';
import AdminKyb from './pages/AdminKyb/AdminKyb';
import AdminSupport from './pages/AdminSupport/AdminSupport';
import AdminPayments from './pages/AdminPayments/AdminPayments';
import SystemSettings from './pages/SystemSettings/SystemSettings';
import AdminUsers from './pages/AdminUsers/AdminUsers';
import AdminUserProfile from './pages/AdminUserProfile/AdminUserProfile';
import AdminRewards from './pages/AdminRewards/AdminRewards';
import AdminPortalInvestments from './pages/AdminPortalInvestments/AdminPortalInvestments';
import AdminCompanyDocuments from './pages/AdminCompanyDocuments/AdminCompanyDocuments';
import AdminPartners from './pages/AdminPartners/AdminPartners';
import AdminContest from './pages/AdminContest/AdminContest';
import AdminMeetings from './pages/AdminMeetings/AdminMeetings';
import AdminLeads from './pages/AdminLeads/AdminLeads';
import AdminAllRequests from './pages/AdminAllRequests/AdminAllRequests';
import AdminContent from './pages/AdminContent/AdminContent';
import AdminCurrencies from './pages/AdminCurrencies/AdminCurrencies';
import AdminBctTokenClaims from './pages/AdminBctTokenClaims/AdminBctTokenClaims';

import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';

const App: React.FC = () => {
  useEffect(() => {
    const onWheel = () => {
      const element = document.activeElement;

      if (
        element instanceof HTMLInputElement
        && element.type === 'number'
      ) {
        element.blur();
      }
    };

    document.addEventListener('wheel', onWheel, { passive: true });

    return () => {
      document.removeEventListener('wheel', onWheel);
    };
  }, []);

  return (
    <ThemeProvider>
      <CurrencyProvider>
        <BrowserRouter>
          <ToastContainer
            position="top-right"
            theme="dark"
            autoClose={3500}
            toastStyle={{
              background: '#14171b',
              border: '1px solid rgba(255,255,255,.1)',
              fontFamily: 'Inter, sans-serif',
              fontSize: 13.5,
            }}
          />

          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/blog/:id" element={<BlogPostDetail />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/register" element={<Register />} />

            <Route
              path="/dashboard"
              element={
                <ProtectedRoute allowedRoles={['user', 'admin']}>
                  <UserDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/kyc"
              element={
                <ProtectedRoute allowedRoles={['user', 'admin']}>
                  <Kyc />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/kyb"
              element={<Navigate to="/dashboard/kyc" replace />}
            />

            <Route
              path="/dashboard/funds"
              element={
                <ProtectedRoute allowedRoles={['user', 'admin']}>
                  <Funds />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/investment"
              element={
                <ProtectedRoute allowedRoles={['user', 'admin']}>
                  <InvestmentDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/payment-history"
              element={
                <ProtectedRoute allowedRoles={['user', 'admin']}>
                  <InrPaymentHistory />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/team"
              element={
                <ProtectedRoute allowedRoles={['user', 'admin']}>
                  <Team />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/meetings"
              element={
                <ProtectedRoute allowedRoles={['user', 'admin']}>
                  <Meetings />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/my-requests"
              element={
                <ProtectedRoute allowedRoles={['user', 'admin']}>
                  <MyRequests />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/rebate"
              element={
                <ProtectedRoute allowedRoles={['user', 'admin']}>
                  <Rebate />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/rank"
              element={
                <ProtectedRoute allowedRoles={['user', 'admin']}>
                  <Rank />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/support"
              element={
                <ProtectedRoute allowedRoles={['user', 'admin']}>
                  <Support />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/partners"
              element={
                <ProtectedRoute allowedRoles={['user', 'admin']}>
                  <PartnerProgram />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/account"
              element={
                <ProtectedRoute allowedRoles={['user', 'admin']}>
                  <AccountSettings />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/portal-investments"
              element={
                <ProtectedRoute allowedRoles={['user', 'admin']}>
                  <PortalInvestments />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/bct-token-claims"
              element={
                <ProtectedRoute allowedRoles={['user', 'admin']}>
                  <BctTokenClaims />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/funds"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminFunds />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/payments"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminPayments />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/ranks"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminRanks />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/settings"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <SystemSettings />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/users"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminUsers />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/users/:id"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminUserProfile />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/rewards"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminRewards />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/partners"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminPartners />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/portal-investments"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminPortalInvestments />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/bct-token-claims"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminBctTokenClaims />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/company-documents"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminCompanyDocuments />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/contest"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminContest />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/kyc"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminKyc />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/kyb"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminKyb />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/meetings"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminMeetings />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/leads"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminLeads />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/all-requests"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminAllRequests />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/content"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminContent />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/currencies"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminCurrencies />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/support"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminSupport />
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </CurrencyProvider>
    </ThemeProvider>
  );
};

export default App;